/* eslint-disable no-undef-init */
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Spinner, Typography } from 'design_system/src';
import Map, {
	Layer,
	MapRef,
	Marker,
	NavigationControl,
	Source,
} from 'react-map-gl';
import mapboxgl from 'utils/mapboxgl';
import type { FeatureCollection } from 'geojson';

import Supercluster from 'supercluster';
import { momentLocal } from 'models/shared/helpers/dates';
import { ReactComponent as Search } from 'design_system/src/static/icons/search.svg';
import { ReactComponent as Close } from 'design_system/src/static/icons/close-sm.svg';
import { ReactComponent as Point } from 'design_system/src/static/icons/target.svg';
import { TileTrackerHistoryLocationDto } from 'models/geozon/interfaces/tile.interface';

import { devicesMapContext } from './DevicesMapContext';

import './devicesMap.scss';
import {
	AddressLocation,
	createClusterMarker,
	createPointMarker,
	hotspotHeatLayerDefinition,
	hotspotLayerDefinition,
} from './DevicesMap.constants';

export const DevicesMap = () => {
	const mapRef = useRef<MapRef>(null);

	const {
		loading,
		locations,
		selectedMarker,
		setSelectedMarker,
		locationsHistory,
		loadingHistory,
	} = useContext(devicesMapContext);

	const [query, setQuery] = useState('');
	const [cursor, setCursor] = useState('grab');

	const [viewState, setViewState] = useState({
		longitude: -99.12766,
		latitude: 19.42847,
		zoom: 10,
	});

	const [selectedHotspot, setSelectedHotspot] =
		useState<TileTrackerHistoryLocationDto>();

	const handleMapClick = (event: mapboxgl.MapLayerMouseEvent) => {
		const map = mapRef.current?.getMap();
		if (map === undefined) return;
		const features = map.queryRenderedFeatures(event.point, {
			layers: ['interest_points_circles'],
		});
		if (!features[0]?.properties?.point) return;
		setSelectedHotspot(JSON.parse(features[0].properties.point));
	};

	const hotspotsLayer = useMemo(() => {
		const source: FeatureCollection = {
			type: 'FeatureCollection',
			features: locationsHistory.map((point, i) => ({
				id: i,
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [point.longitude, point.latitude],
				},
				properties: {
					weight: 6 / locationsHistory.length,
					point: JSON.stringify(point),
				},
			})),
		};
		return source?.features?.length ? (
			<Source type="geojson" data={source}>
				<Layer {...hotspotHeatLayerDefinition} />
				<Layer {...hotspotLayerDefinition} />
			</Source>
		) : null;
	}, [locationsHistory]);

	const supercluster = useMemo(
		() =>
			new Supercluster({
				radius: 75,
				maxZoom: 20,
			}),
		[],
	);

	const handleClusterClick = useCallback(
		(cluster) => {
			const clusterExpansionZoom = supercluster.getClusterExpansionZoom(
				cluster.id,
			);
			mapRef.current?.flyTo({
				center: cluster.geometry.coordinates,
				zoom: clusterExpansionZoom,
			});
		},
		[supercluster, viewState],
	);

	const onClickHandler = (point: AddressLocation) => {
		setSelectedHotspot(undefined);
		if (selectedMarker?.id === point.id) {
			setSelectedMarker(undefined);
			return;
		}
		setSelectedMarker(point);
	};

	useEffect(() => {
		if (selectedMarker) {
			mapRef.current?.flyTo({
				center: [
					selectedMarker.coordinates.long,
					selectedMarker.coordinates.lat,
				],
				zoom: 15,
			});
		}
	}, [selectedMarker]);

	const markers = useMemo(() => {
		const filtered = locations.filter((point) =>
			point.vehicleId.includes(query),
		);
		supercluster.load(
			filtered.map((point) => ({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [point.coordinates.long, point.coordinates.lat],
				},
				properties: point,
			})),
		);

		const baseMarkers = supercluster
			.getClusters([-180, -85, 180, 85], Math.round(viewState.zoom))
			.map((cluster) => {
				if (cluster.properties.cluster) {
					return createClusterMarker(cluster, handleClusterClick);
				}
				const point = cluster.properties as AddressLocation;
				return createPointMarker(
					cluster,
					point,
					onClickHandler,
					selectedMarker?.id === point.id,
				);
			});

		const selectedHotspotMarker =
			selectedHotspot && selectedMarker
				? [
						<Marker
							key="hotspot_data"
							longitude={selectedHotspot.longitude}
							latitude={selectedHotspot.latitude}
							onClick={() => setSelectedHotspot(undefined)}
						>
							<div className="br_xxl main_marker cursor_pointer primary">
								<div className="marker_info p_y_xxs p_x_md flex_center flex_gap_sm">
									<Point className="dim_lg icon" />
									<Typography weight="600" scale="xsmall">
										{selectedMarker.vehicleId}
									</Typography>
								</div>
								<div className="marker_details p_x_md flex_center_col">
									<Typography weight="600" scale="xxsmall">
										{momentLocal(selectedHotspot.timestamp).format(
											'DD/MM/YYYY',
										)}
									</Typography>
									<Typography weight="400" scale="xxsmall">
										{momentLocal(selectedHotspot.timestamp).format('HH:mm')}
									</Typography>
								</div>
								<span className="arrow" />
							</div>
						</Marker>,
				  ]
				: [];

		return [...baseMarkers, ...selectedHotspotMarker];
	}, [
		locations,
		supercluster,
		viewState.zoom,
		query,
		selectedMarker,
		selectedHotspot,
	]);

	return (
		<div className="dim_100_per flex_content_col inner_container bg_neutral_300">
			<div className="w_100_per bg_neutral_0 flex_header p_x_lg p_y_md">
				<Typography scale="heading2" weight="600" className="text_primary_300">
					Mapa GPS
				</Typography>
			</div>
			<div className="flex_grow_1 overflow_hidden pos_relative">
				<div className="dim_100_per flex_content_col inner_container bg_neutral_300">
					{loading && (
						<div className="pos_absolute center_x br_md bg_neutral_0 p_md z_index_30 m_t_xl flex_center flex_gap_md">
							<Spinner className="dim_xxl" />
							<div>
								<Typography scale="medium" weight="600" className="m_b_xxs">
									Cargando mapa...
								</Typography>
								<Typography scale="small" weight="400" className="">
									📍 No tardaremos mucho.
								</Typography>
							</div>
						</div>
					)}
					{loadingHistory && (
						<div className="pos_absolute center_x br_md bg_neutral_0 p_md z_index_30 m_t_xl flex_center flex_gap_md">
							<Spinner className="dim_xxl" />
							<div>
								<Typography scale="medium" weight="600" className="m_b_xxs">
									Cargando histórico...
								</Typography>
								<Typography scale="small" weight="400" className="">
									📍 No tardaremos mucho.
								</Typography>
							</div>
						</div>
					)}
					<div className="repossession_map_input_cont ">
						<input
							type="text"
							className="repossession_map_input shadow_hard"
							placeholder="Buscar Moto"
							value={query}
							onChange={(e) => setQuery(e.target.value.toUpperCase())}
						/>
						<Search className="repossession_map_search_icon dim_lg text_primary_300" />
					</div>
					{selectedMarker && (
						<div className="devices_map_status m_b_md">
							<div className=" bg_neutral_0 br_md m_b_md shadow_hard overflow_hidden">
								<div className="p_y_md p_x_sm border_b_1 border_primary_100 border_b_solid flex_center flex_justify_between">
									<Typography scale="large" weight="600">
										{selectedMarker.vehicleId}
									</Typography>
									<Close
										className="dim_xl border_transparent cursor_pointer"
										onClick={() => setSelectedMarker(undefined)}
									/>
								</div>
								<div className="p_y_md p_x_sm border_b_1 border_primary_100 border_b_solid display_flex flex_align_start flex_gap_xs flex_col">
									<Typography scale="medium" weight="600">
										Fecha:
										<Typography
											scale="medium"
											weight="400"
											component="span"
											className="m_l_md"
										>
											{momentLocal(selectedMarker.createdAt).format(
												'DD/MM/YYYY HH:mm',
											)}
										</Typography>
									</Typography>
									{selectedMarker.tileId ? (
										<Typography scale="medium" weight="600">
											Histórico:
											<Typography
												scale="medium"
												weight="400"
												component="span"
												className="m_l_md"
											>
												{locationsHistory.length
													? `${locationsHistory.length} puntos (90 días)`
													: 'Sin puntos históricos'}
											</Typography>
										</Typography>
									) : (
										<Typography scale="medium" weight="400">
											Este punto no tiene Tile asignado, no hay histórico
											disponible.
										</Typography>
									)}
								</div>
							</div>
						</div>
					)}
					<Map
						mapLib={mapboxgl}
						mapboxAccessToken={process.env.REACT_APP_MAPBOX_API_KEY || ''}
						onMove={(evt) => setViewState(evt.viewState)}
						mapStyle="mapbox://styles/mapbox/streets-v9"
						reuseMaps
						ref={mapRef}
						interactiveLayerIds={['interest_points_circles']}
						onMouseEnter={() => setCursor('pointer')}
						onMouseLeave={() => setCursor('grab')}
						onClick={handleMapClick}
						cursor={cursor}
						{...viewState}
					>
						<NavigationControl position="bottom-right" />
						{hotspotsLayer}
						{markers}
					</Map>
				</div>
			</div>
		</div>
	);
};
